import React from 'react';
import { useLocation } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';

/**
 * Loading fallbacks for the public marketing pages.
 *
 * The HOME page ("/") has a distinctive centered hero, so it gets a
 * hero-shaped skeleton. The OTHER landing pages (use-cases, privacy, terms,
 * contact, home-classic) have varied content (legal copy, forms, grids), so a
 * hero skeleton would mismatch them - they get a neutral brand spinner instead.
 *
 * Built with inline styles + the shared `Skeleton` shimmer primitive (not
 * Tailwind): `landing.css` and the scoped Tailwind tokens only load with the
 * LandingLayout chunk, so during the OUTER Suspense (layout still loading)
 * Tailwind classes wouldn't be styled yet. Inline styles always render.
 *
 *   <LandingOuterFallback />  outer boundary (LandingLayout loading): hero
 *                             skeleton on "/", neutral spinner elsewhere.
 *   <LandingHeroSkeleton />   "/" page content loading (real Header already up).
 *   <LandingLoader />         neutral spinner for the other landing pages.
 */

// Inline styles can't do media queries / keyframes; inject once.
const STYLE_ID = 'landing-skeleton-style';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.innerHTML =
    `.lsk-nav{display:none;gap:28px}@media(min-width:768px){.lsk-nav{display:flex}}` +
    `@keyframes lsk-spin{to{transform:rotate(360deg)}}`;
  document.head.appendChild(el);
}

const blob = (s: React.CSSProperties): React.CSSProperties => ({
  position: 'absolute',
  pointerEvents: 'none',
  ...s,
});

const Blobs: React.FC = () => (
  <div
    style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}
  >
    <div
      style={blob({
        top: -160,
        right: -160,
        width: 800,
        height: 600,
        borderRadius: '40%',
        background: 'rgba(45,212,191,0.22)',
        filter: 'blur(100px)',
      })}
    />
    <div
      style={blob({
        top: 0,
        right: 0,
        width: 600,
        height: 500,
        borderRadius: '50%',
        background: 'rgba(254,240,138,0.32)',
        filter: 'blur(120px)',
      })}
    />
    <div
      style={blob({
        top: 80,
        left: -80,
        width: 500,
        height: 500,
        borderRadius: '45%',
        background: 'rgba(45,212,191,0.22)',
        filter: 'blur(100px)',
      })}
    />
  </div>
);

const Spinner: React.FC = () => (
  <div
    style={{
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: '3px solid rgba(20,155,158,0.18)',
      borderTopColor: '#0f7a7d',
      animation: 'lsk-spin 0.8s linear infinite',
    }}
  />
);

const HeaderBar: React.FC = () => (
  <div
    style={{
      maxWidth: 1280,
      margin: '0 auto',
      padding: '20px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      zIndex: 1,
    }}
  >
    <Skeleton style={{ width: 130, height: 36, borderRadius: 8 }} />
    <div className="lsk-nav">
      {[60, 52, 68, 56].map((w, i) => (
        <Skeleton key={i} style={{ width: w, height: 14, borderRadius: 6 }} />
      ))}
    </div>
    <Skeleton style={{ width: 120, height: 40, borderRadius: 999 }} />
  </div>
);

const Hero: React.FC = () => (
  <>
    {/* Headline + subtext + CTAs — centered first viewport. No badge/pill: the
        real hero opens straight on the headline. */}
    <div
      style={{
        maxWidth: 896,
        margin: '0 auto',
        padding: '48px 16px',
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <Skeleton style={{ width: '90%', height: 48, borderRadius: 10 }} />
        <Skeleton style={{ width: '66%', height: 48, borderRadius: 10 }} />
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: 680,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Skeleton style={{ width: '92%', height: 14, borderRadius: 6 }} />
        <Skeleton style={{ width: '70%', height: 14, borderRadius: 6 }} />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 16,
          paddingTop: 16,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <Skeleton style={{ width: 190, height: 52, borderRadius: 999 }} />
        <Skeleton style={{ width: 190, height: 52, borderRadius: 999 }} />
      </div>
    </div>
    {/* Demo-video / platform-preview block below the CTAs (max-w-7xl, 16:9). */}
    <div
      style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '0 16px 80px',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Skeleton style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 12 }} />
    </div>
  </>
);

/** Hero-only fallback for "/" content (real Header already mounted). */
export const LandingHeroSkeleton: React.FC = () => <Hero />;

/** Neutral brand spinner - for the non-home landing pages while their chunk
 *  loads. `fullScreen` adds the brand background (used before the layout's own
 *  background exists, i.e. the outer boundary). */
export const LandingLoader: React.FC<{ fullScreen?: boolean }> = ({ fullScreen }) => (
  <div
    style={{
      minHeight: fullScreen ? '100vh' : '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: fullScreen ? '#fff' : 'transparent',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {fullScreen && <Blobs />}
    <div style={{ position: 'relative', zIndex: 1 }}>
      <Spinner />
    </div>
  </div>
);

/** Full-shell hero skeleton (header bar + hero) - only for "/" before the
 *  LandingLayout chunk has rendered the real Header. */
const LandingSkeleton: React.FC = () => (
  <div style={{ minHeight: '100vh', background: '#fff', position: 'relative', overflow: 'hidden' }}>
    <Blobs />
    <HeaderBar />
    <Hero />
  </div>
);

/** Outer boundary fallback (LandingLayout itself loading). Path-aware: the
 *  home hero skeleton on "/", a neutral spinner on every other landing page -
 *  so the hero shape never flashes on use-cases/privacy/terms/contact. */
export const LandingOuterFallback: React.FC = () => {
  const { pathname } = useLocation();
  return pathname === '/' ? <LandingSkeleton /> : <LandingLoader fullScreen />;
};

export default LandingSkeleton;
